import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { motion } from "framer-motion";
import sha256 from "js-sha256";
import JSEncrypt from "jsencrypt";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthenticationApi } from "../../../api/Authentication/LoginApi";
import { ERP_BASE_URL } from "../../../config/apiConfig";

// Replace this with your actual logo import or URL
const logoUrl = "/assets/zypp-logo.png";

const allowedAuthorities = [
  "ROLE_ERP_ADMIN",
  "ROLE_ERP_MANAGER",
  "ROLE_ERP_IT_ADMIN",
  "ROLE_INVENTORY_MANAGER",
  "ROLE_ERP_DATA",
  "ROLE_ERP_REFURB_VENDOR",
  "ROLE_ERP_WARRANTY_VENDOR",
  "ROLE_FRANCHISE_INVENTORY_MANAGER",
  "ROLE_ERP_FRANCHISE_ADMIN"
];

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasSsoTicket = Boolean(searchParams.get("ticket"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loader, setLoader] = useState<boolean>(hasSsoTicket);

  // Forgot Password states
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: token and new password
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [resetLoader, setResetLoader] = useState(false);

  const hexToBytes = (hex: string) => {
    const bytes: number[] = [];
    for (let i = 0; i < hex.length; i += 2) {
      bytes.push(parseInt(hex.substr(i, 2), 16));
    }
    return bytes;
  };

  const handleForgotPasswordOpen = () => {
    setForgotPasswordOpen(true);
    setResetStep(1);
    setResetEmail("");
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
    setResetError(null);
    setResetSuccess(null);
  };

  const handleForgotPasswordClose = () => {
    setForgotPasswordOpen(false);
    setResetStep(1);
    setResetEmail("");
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
    setResetError(null);
    setResetSuccess(null);
  };

  const handleSendResetLink = async () => {
    setResetLoader(true);
    setResetError(null);
    setResetSuccess(null);

    try {
      const response = await axios.get(
        `${ERP_BASE_URL}/erp/user/change/password/request`,
        {
          params: { email: resetEmail }
        }
      );
      setResetLoader(false);
      setResetSuccess("Mail sent successfully to your email! Please check your inbox and click the reset link.");
      setTimeout(() => {
        handleForgotPasswordClose();
      }, 30000); // Close after 30 seconds
    } catch (error: any) {
      console.error("Send reset link error:", error);
      setResetLoader(false);
      setResetError(error.response?.data?.message || "Failed to send reset link. Please try again.");
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match!");
      return;
    }
    if (newPassword.length < 6) {
      setResetError("Password must be at least 6 characters long!");
      return;
    }

    setResetLoader(true);
    setResetError(null);
    setResetSuccess(null);

    try {
      // Encrypt the new password like in login
      const timestamp = String(Date.now());
      const hash = sha256.sha256(newPassword + timestamp);
      const hashBytes = hexToBytes(hash);
      const hashBase64 = btoa(String.fromCharCode(...hashBytes));
      const data = `${newPassword}:${timestamp}:${hashBase64}`;
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(
        "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAg79k2m8PlGNIyJum2uzGqnhdjNqc90SF5FwoOlU7I6Hy6xh36K2qzw1Q5wLcfM+KojFy0xLzKf/u03Wm9S/SmbwvzlXaCq5uYEtVqJBxCz3Y+ZyTRlWyWDJki+OvGYqGbw1EmmKFt2SaOjsE/e+lo6KQpFZwGuaJ1ZKQjCbKRdmkOUAWIjK51a3r8kTnq7XIMf9iXaHX5qvTS/BMcRrXTQ5/JGeuy9OCaJ0S7YrSINZymd0puA0IH87QxDmnUoq+pJwo8l67MFwK7rLyCDSRGwdVY4kLQw8T25k53eoH6+ZUMLydkR4X/ryXYERIw/OwPWpoioHARKzyA/ARA2jkTQIDAQAB"
      );

      const encryptedPassword = encrypt.encrypt(data);
      if (!encryptedPassword) throw new Error("Encryption failed. Invalid data.");

      const response = await axios.put(
        `${ERP_BASE_URL}/erp/user/change/password`,
        {
          newPassword: encryptedPassword,
          confirmPassword: encryptedPassword
        },
        {
          params: { token: resetToken }
        }
      );
      setResetLoader(false);
      setResetSuccess("Password reset successfully! You can now login.");
      setTimeout(() => {
        handleForgotPasswordClose();
      }, 2000);
    } catch (error: any) {
      console.error("Reset password error:", error);
      setResetLoader(false);
      setResetError(error.response?.data?.message || "Failed to reset password. Please try again.");
    }
  };

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoader(true);
    setLoginError(null);
    try {
      const timestamp = String(Date.now());
      const hash = sha256.sha256(password + timestamp);
      const hashBytes = hexToBytes(hash);
      const hashBase64 = btoa(String.fromCharCode(...hashBytes));
      const data = `${password}:${timestamp}:${hashBase64}`;
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(
        "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAg79k2m8PlGNIyJum2uzGqnhdjNqc90SF5FwoOlU7I6Hy6xh36K2qzw1Q5wLcfM+KojFy0xLzKf/u03Wm9S/SmbwvzlXaCq5uYEtVqJBxCz3Y+ZyTRlWyWDJki+OvGYqGbw1EmmKFt2SaOjsE/e+lo6KQpFZwGuaJ1ZKQjCbKRdmkOUAWIjK51a3r8kTnq7XIMf9iXaHX5qvTS/BMcRrXTQ5/JGeuy9OCaJ0S7YrSINZymd0puA0IH87QxDmnUoq+pJwo8l67MFwK7rLyCDSRGwdVY4kLQw8T25k53eoH6+ZUMLydkR4X/ryXYERIw/OwPWpoioHARKzyA/ARA2jkTQIDAQAB"
      );

      const encryptedData = encrypt.encrypt(data);
      if (!encryptedData) throw new Error("Encryption failed. Invalid data.");

      const response = await AuthenticationApi.api.onlineUserAuthentication(
        email,
        encryptedData
      );

      setLoader(false);
      if (response.code === 200 && response.data && response.data.token) {
        const userAuthority = response.data.userInfo.authority[0]?.authority;

        if (allowedAuthorities.includes(userAuthority)) {
          localStorage.setItem("token", response.data.token);
          const authority = userAuthority || "ADMIN";
          localStorage.setItem("authority", authority);
          localStorage.setItem("name", response.data.userInfo.username || "");
          navigate(
            authority === "ROLE_ERP_IT_ADMIN"
              ? "/user-management/edit"
              : authority === "ROLE_FRANCHISE_INVENTORY_MANAGER"
              ? "/sales-order"
              : authority === "ROLE_ERP_REFURB_VENDOR" || authority === "ROLE_ERP_WARRANTY_VENDOR"
              ? "/warranty-requests"
              : "/home"
          );
        } else {
          setLoginError(
            "Access Denied. You do not have the required authority."
          );
        }
      } else {
        setLoginError(response.message || "Access Denied");
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoader(false);
      setLoginError("Invalid Credentials or Network Error");
    }
  };

  useEffect(() => {
    const ticket = searchParams.get("ticket");
    if (ticket) {
      setLoader(true);
      setLoginError(null);

      AuthenticationApi.api
        .authenticateWithSsoTicket(ticket)
        .then((response) => {
          if (![200, 2000].includes(response.code) || !response.data?.token) {
            throw new Error(response.message || "SSO login failed");
          }

          const userAuthority = response.data.userInfo.authority[0]?.authority;
          if (!allowedAuthorities.includes(userAuthority)) {
            throw new Error("Access Denied. You do not have the required authority.");
          }

          localStorage.setItem("authority", userAuthority);
          localStorage.setItem("name", response.data.userInfo.username || "");
          navigate(
            userAuthority === "ROLE_ERP_IT_ADMIN"
              ? "/user-management/create"
              : userAuthority === "ROLE_FRANCHISE_INVENTORY_MANAGER"
              ? "/sales-order"
              : userAuthority === "ROLE_ERP_REFURB_VENDOR" || userAuthority === "ROLE_ERP_WARRANTY_VENDOR"
              ? "/warranty-requests"
              : "/home",
            { replace: true }
          );
        })
        .catch((error: Error) => {
          setLoginError(error.message || "SSO login failed");
          setLoader(false);
        });
      return;
    }

    const token = localStorage.getItem("token");
    const authority = localStorage.getItem("authority");
    if (token && allowedAuthorities.includes(authority || "")) {
      navigate(
        authority === "ROLE_ERP_IT_ADMIN"
          ? "/user-management/create"
          : authority === "ROLE_FRANCHISE_INVENTORY_MANAGER"
          ? "/sales-order"
          : authority === "ROLE_ERP_REFURB_VENDOR" || authority === "ROLE_ERP_WARRANTY_VENDOR"
          ? "/warranty-requests"
          : "/home"
      );
    }
  }, [navigate, searchParams]);

  if (hasSsoTicket && loader) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        position: "relative",
        overflow: "hidden",
      }}>
      {/* Animated background elements - subtle geometric shapes */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}>
        <motion.div
          style={{
            position: "absolute",
            width: "600px",
            height: "600px",
            borderRadius: "40% 60% 70% 30% / 40% 50% 60% 50%",
            background:
              "linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.05) 100%)",
            top: "-300px",
            left: "-200px",
          }}
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.div
          style={{
            position: "absolute",
            width: "400px",
            height: "400px",
            borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.2) 100%)",
            bottom: "-200px",
            right: "-200px",
          }}
          animate={{
            rotate: [0, -360],
          }}
          transition={{
            duration: 45,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </Box>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 5, md: 6 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            position: "relative",
            overflow: "hidden",
            maxWidth: "480px",
            width: "100%",
            mx: 2,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.08)",
          }}>
          {/* Subtle top accent */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "80px",
              height: "3px",
              background:
                "linear-gradient(90deg, transparent, #000, transparent)",
              opacity: 0.2,
            }}
          />

          {/* Logo */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}>
            <Box
              component="img"
              src={logoUrl}
              alt="Logo"
              sx={{
                height: 48,
                mb: 4,
                opacity: 0.8,
              }}
            />
          </motion.div>

          {/* Welcome Text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 300,
                color: "#1a1a1a",
                mb: 1,
                letterSpacing: "-0.5px",
                textAlign: "center",
              }}>
              Welcome back
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#6b7280",
                mb: 5,
                textAlign: "center",
                fontWeight: 400,
              }}>
              Enter your credentials to access your account
            </Typography>
          </motion.div>

          {/* Login Form */}
          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{ width: "100%", mt: 1 }}>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                size="medium"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f9fafb",
                    transition: "all 0.2s",
                    "& fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.08)",
                    },
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.12)",
                      },
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      "& fieldset": {
                        borderColor: "#374151",
                        borderWidth: "1px",
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#6b7280",
                    "&.Mui-focused": {
                      color: "#374151",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon
                        sx={{ color: "#9ca3af", fontSize: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                size="medium"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  mb: 4,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f9fafb",
                    transition: "all 0.2s",
                    "& fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.08)",
                    },
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.12)",
                      },
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      "& fieldset": {
                        borderColor: "#374151",
                        borderWidth: "1px",
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#6b7280",
                    "&.Mui-focused": {
                      color: "#374151",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOpenIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{
                          color: "#9ca3af",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#6b7280",
                          },
                        }}>
                        {showPassword ? (
                          <Visibility sx={{ fontSize: 20 }} />
                        ) : (
                          <VisibilityOff sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </motion.div>

            {loginError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: "12px",
                    backgroundColor: "#fef2f2",
                    color: "#991b1b",
                    "& .MuiAlert-icon": {
                      color: "#dc2626",
                    },
                    border: "1px solid #fecaca",
                  }}>
                  {loginError}
                </Alert>
              </motion.div>
            )}

            {/* Forgot Password Link */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                onClick={handleForgotPasswordOpen}
                sx={{
                  textTransform: 'none',
                  color: '#6b7280',
                  fontSize: '14px',
                  fontWeight: 500,
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#374151',
                    textDecoration: 'underline',
                  },
                }}>
                Forgot Password?
              </Button>
            </Box>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loader}
                sx={{
                  mt: 1,
                  mb: 3,
                  py: 1.75,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  letterSpacing: "0.3px",
                  background: "#1a1a1a",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "#000",
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  "&:disabled": {
                    background: "#e5e7eb",
                    color: "#9ca3af",
                  },
                }}>
                {loader ? (
                  <CircularProgress size={22} sx={{ color: "white" }} />
                ) : (
                  "Sign in"
                )}
              </Button>
            </motion.div>
          </Box>
        </Paper>
      </motion.div>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleForgotPasswordClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2,
          },
        }}>
        <DialogTitle sx={{ fontSize: '24px', fontWeight: 600, pb: 1 }}>
          {resetStep === 1 && 'Reset Password'}
          {resetStep === 2 && 'Enter Token & New Password'}
        </DialogTitle>
        <DialogContent>
          {resetStep === 1 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter your actual email address (e.g. johndoe@zypp.app or  johndoe@zypp.in), and we'll send you a password reset link
              </Typography>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {resetStep === 2 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please enter the token from your email and create a new password.
              </Typography>
              <TextField
                fullWidth
                label="Reset Token"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                placeholder="Enter the token from your email"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
              />
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOpenIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        sx={{ color: '#9ca3af' }}>
                        {showNewPassword ? (
                          <Visibility sx={{ fontSize: 20 }} />
                        ) : (
                          <VisibilityOff sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOpenIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: '#9ca3af' }}>
                        {showConfirmPassword ? (
                          <Visibility sx={{ fontSize: 20 }} />
                        ) : (
                          <VisibilityOff sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {resetError && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                borderRadius: '12px',
                backgroundColor: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
              }}>
              {resetError}
            </Alert>
          )}

          {resetSuccess && (
            <Alert
              severity="success"
              sx={{
                mt: 2,
                borderRadius: '12px',
                backgroundColor: '#f0fdf4',
                color: '#166534',
                border: '1px solid #bbf7d0',
              }}>
              {resetSuccess}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleForgotPasswordClose}
            sx={{
              textTransform: 'none',
              color: '#6b7280',
              borderRadius: '12px',
            }}>
            Cancel
          </Button>
          {resetStep === 1 && (
            <Button
              onClick={handleSendResetLink}
              disabled={!resetEmail || resetLoader}
              variant="contained"
              sx={{
                textTransform: 'none',
                borderRadius: '12px',
                background: '#1a1a1a',
                px: 3,
                '&:hover': {
                  background: '#000',
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af',
                },
              }}>
              {resetLoader ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Send Reset Link'}
            </Button>
          )}
          {resetStep === 2 && (
            <>
              <Button
                onClick={() => setResetStep(1)}
                disabled={resetLoader}
                sx={{
                  textTransform: 'none',
                  color: '#374151',
                  borderRadius: '12px',
                }}>
                Back
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={!resetToken || !newPassword || !confirmPassword || resetLoader}
                variant="contained"
                sx={{
                  textTransform: 'none',
                  borderRadius: '12px',
                  background: '#1a1a1a',
                  px: 3,
                  '&:hover': {
                    background: '#000',
                  },
                  '&:disabled': {
                    background: '#e5e7eb',
                    color: '#9ca3af',
                  },
                }}>
                {resetLoader ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Reset Password'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
